import type { SxProps, Theme } from '@mui/material';

import { caloTheme } from '../assets/themes/calo';

const { palette } = caloTheme;

export const PILL_RADIUS = 999;

const ring = (color: string) => ({ outline: `3px solid ${color}`, outlineOffset: '2px' });

export const focusRing = ring(palette.primary800);

// primary800 is 1.26:1 against a dark bar; white clears the 3:1 a focus indicator needs there.
export const focusRingOnDark = ring(palette.white);

// For headings and status lines that take focus from script after a step changes: not controls, so no ring.
export const focusTarget = { '&:focus-visible': { outline: 'none' } } as const;

export const motion = {
  transition: caloTheme.transitions.create(['background-color', 'transform'], {
    duration: 120,
    easing: caloTheme.transitions.easing.easeOut
  }),
  '&:active': { transform: 'scale(0.98)' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' }
};

const base = {
  textTransform: 'none',
  borderRadius: `${PILL_RADIUS}px`,
  boxShadow: 'none',
  '&.Mui-focusVisible, &:focus-visible': focusRing,
  ...motion
} as const;

// White on the brand green clears only large text, so every filled label is 20px bold.
export const primaryButtonSx: SxProps<Theme> = {
  ...base,
  minHeight: 48,
  px: 3,
  fontSize: 20,
  lineHeight: '24px',
  fontWeight: 700,
  color: palette.white,
  backgroundColor: palette.primary.main,
  '&:hover': { backgroundColor: palette.primary700, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: palette.neutral100, color: palette.neutral800 }
};

export const outlineButtonSx: SxProps<Theme> = {
  ...base,
  minHeight: 48,
  px: 3,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 700,
  color: palette.primary800,
  backgroundColor: palette.white,
  border: `1px solid ${palette.neutral700}`,
  '&:hover': { backgroundColor: palette.neutral50, border: `1px solid ${palette.neutral700}` }
};

// The answer the chef gave: the outline button filled and edged in the brand colours.
export const selectedOutlineButtonSx: SxProps<Theme> = {
  ...outlineButtonSx,
  backgroundColor: palette.primary100,
  border: `1px solid ${palette.primary800}`,
  boxShadow: `inset 0 0 0 1px ${palette.primary800}`,
  '&:hover': { backgroundColor: palette.primary100, border: `1px solid ${palette.primary800}` }
};

export const smallOutlineButtonSx: SxProps<Theme> = { ...outlineButtonSx, minHeight: 40, fontSize: 14 };

export const quietLinkSx: SxProps<Theme> = {
  ...base,
  minHeight: 48,
  px: 1.5,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 700,
  color: palette.primary800,
  '&:hover': { backgroundColor: palette.neutral100 }
};

export const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

// The chef type ramp: arm's length on a wall tablet, one step larger from the tablet width.
export const chefText = {
  title: { fontSize: { xs: 18, md: 20 }, lineHeight: '28px', fontWeight: 700 },
  body: { fontSize: { xs: 16, md: 18 }, lineHeight: { xs: '24px', md: '28px' } },
  meta: { fontSize: { xs: 14, md: 16 }, lineHeight: { xs: '20px', md: '24px' } }
} as const;
